import { isValidChartRecid } from '@/composables/isValidChartRecid';
import { isValidChartListListNum } from '@/composables/isValidChartListListNum';
import { extractStyleCacheId } from '@/utils/extractStyleCacheId';
import { buildUrlString } from '@/utils/buildUrlString';
import { getStyleCacheSettingsOverrides } from '@/utils/getStyleCacheSettingsOverrides';
import { JsonLayerChart } from '@/interfaces/json-layer-responses/jsonLayerChart';
import { SimpleFrontendChart } from '@/interfaces/chart';
import { stripHtmlFromString } from '@/plugins/vue-sanitize-html';

export function parseCharts(rawCharts: JsonLayerChart[]): SimpleFrontendChart[] {
  if (!rawCharts) {
    throw new Error(`Unable to parse Charts - rawCharts is falsy: ${typeof rawCharts} ${rawCharts}.`);
  }

  if (!Array.isArray(rawCharts)) {
    throw new Error(`Unable to parse Charts - rawCharts is truthy but is not an array: ${typeof rawCharts}.`);
  }

  const charts: SimpleFrontendChart[] = [];

  for (let i = 0; i < rawCharts.length; i++) {
    const rawChart = rawCharts[i];

    if (!rawChart) {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} - rawChart is falsy: ${typeof rawChart} ${rawChart}.`);
      continue;
    }

    if (typeof rawChart !== 'object') {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} - rawChart is truthy but is not an object: ${typeof rawChart} ${rawChart}.`);
      continue;
    }

    if (!isValidChartRecid(rawChart.recID)) {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} - rawChart.recID is not a valid chart recid: ${typeof rawChart.recID} ${rawChart.recID}.`);
      continue;
    }

    if (!isValidChartListListNum(rawChart.listNum)) {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} with recid: ${rawChart.recID} - rawChart.listNum is not a valid ChartList listNum: ${typeof rawChart.listNum} ${rawChart.listNum}.`);
      continue;
    }

    if (typeof rawChart.description !== 'string') {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} with recid: ${rawChart.recID} and listNum: ${rawChart.listNum} - rawChart.description is not a string: ${typeof rawChart.description} ${rawChart.description}.`);
      continue;
    }

    if (!rawChart.description.trim()) {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} with recid: ${rawChart.recID} and listNum: ${rawChart.listNum} - trimmed rawChart.description is an empty string.`);
      continue;
    }

    if (typeof rawChart.symbol !== 'string') {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} with recid: ${rawChart.recID} and listNum: ${rawChart.listNum} - rawChart.symbol is not a string: ${typeof rawChart.symbol} ${rawChart.symbol}.`);
      continue;
    }

    if (!rawChart.symbol.trim()) {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} with recid: ${rawChart.recID} and listNum: ${rawChart.listNum} - trimmed rawChart.symbol is an empty string.`);
      continue;
    }

    if (typeof rawChart.style !== 'string') {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} with recid: ${rawChart.recID} and listNum: ${rawChart.listNum} - rawChart.style is not a string: ${typeof rawChart.style} ${rawChart.style}.`);
      continue;
    }

    const rawChartStyle = rawChart.style.trim();

    if (!rawChartStyle) {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} with recid: ${rawChart.recID} and listNum: ${rawChart.listNum} - trimmed rawChart.style is an empty string.`);
      continue;
    }

    const isPointAndFigureChart = !rawChartStyle.includes('&id=');
    const styleCacheId = isPointAndFigureChart ? rawChartStyle : extractStyleCacheId(rawChartStyle);

    if (!styleCacheId) {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} with recid: ${rawChart.recID} and listNum: ${rawChart.listNum} - trimmed rawChart.style is not a valid stylecache id: ${typeof rawChartStyle} ${rawChartStyle}.`);
      continue;
    }

    if (!isPointAndFigureChart && !styleCacheId.startsWith('p')) {
      window.sc3WorkbenchLogger.error(`Unable to parse chart at index ${i} with recid: ${rawChart.recID} and listNum: ${rawChart.listNum} - trimmed rawChart.style is a valid stylecache id but does not start with a "p": ${typeof rawChartStyle} ${rawChartStyle}.`);
      continue;
    }

    const name = rawChart.description.trim();
    const symbol = rawChart.symbol.replace(/\s/g, '').toUpperCase();
    const htmlSanitizedName = stripHtmlFromString(name);
    const searchTags = [htmlSanitizedName, symbol];
    const recid = Number(rawChart.recID);
    const annotations = typeof rawChart.annotations === 'string' ? rawChart.annotations : '';
    const comments = typeof rawChart.comments === 'string' ? rawChart.comments.replace(/<br>/g, '\n') : '';
    const chartListListNum = Number(rawChart.listNum);

    let thumbnailUrl = '';

    if (isPointAndFigureChart) {
      const parameterObject = { c: `${encodeURIComponent(symbol)},${styleCacheId}`, pnf: 'y' };
      thumbnailUrl = buildUrlString('/pnf/chart', { areParametersAlreadyUrlEncoded: true, parameters: parameterObject });
    } else {
      const parameterObject = { i: styleCacheId, s: encodeURIComponent(symbol), a: `${recid}` };
      thumbnailUrl = buildUrlString('/c-sc/sc', { areParametersAlreadyUrlEncoded: true, parameters: parameterObject });
    }

    charts.push({
      name,
      symbol,
      htmlSanitizedName,
      thumbnailUrl,
      searchTags,
      styleCacheId,
      styleCacheSettingsOverrides: getStyleCacheSettingsOverrides(rawChartStyle),
      recid,
      comments,
      chartListListNum,
      annotations,
      temporaryName: name,
      isForPayingMembersOnly: true,
      isPointAndFigureChart
    });
  }

  return charts;
}
